import type { CrmSettings, WhatsAppTemplateSetting } from "../types/crm";
import { whatsappTemplates } from "./whatsapp";

function validText(value: unknown, min: number, max: number) {
  return typeof value === "string" && value.trim().length >= min && value.trim().length <= max;
}

function validOptionList(value: unknown) {
  return Array.isArray(value) && value.length <= 100 && value.every((item) => validText(item, 1, 200));
}

function isCrmSettings(value: unknown): value is CrmSettings {
  if (!value || typeof value !== "object") return false;
  const settings = value as Record<string, unknown>;
  const scoring = settings.lead_scoring;
  const prospectScoring = settings.prospect_scoring;
  const templates = settings.whatsapp_templates;
  return validText(settings.company_name, 1, 160)
    && validText(settings.timezone, 1, 100)
    && typeof settings.default_currency === "string" && /^[A-Z]{3}$/.test(settings.default_currency)
    && typeof settings.follow_up_default_days === "number" && Number.isInteger(settings.follow_up_default_days) && settings.follow_up_default_days >= 1 && settings.follow_up_default_days <= 90
    && typeof settings.email_signature === "string" && settings.email_signature.length <= 5000
    && Boolean(scoring) && typeof scoring === "object" && !Array.isArray(scoring) && Object.values(scoring as Record<string, unknown>).every((item) => typeof item === "number" && Number.isFinite(item) && item >= -100 && item <= 100)
    && Boolean(prospectScoring) && typeof prospectScoring === "object" && !Array.isArray(prospectScoring) && Object.values(prospectScoring as Record<string, unknown>).every((item) => typeof item === "number" && Number.isFinite(item) && item >= -100 && item <= 100)
    && Array.isArray(templates) && templates.length <= 50 && templates.every((item) => Boolean(item) && typeof item === "object" && typeof item.key === "string" && /^[a-z0-9_]{1,80}$/.test(item.key) && validText(item.label, 1, 100) && validText(item.message, 1, 4096))
    && validOptionList(settings.customer_sources)
    && validOptionList(settings.product_interests);
}

export const crmSettingsSchema = {
  safeParse(value: unknown): { success: true; data: CrmSettings } | { success: false; error: Error } {
    return isCrmSettings(value) ? { success: true, data: structuredClone(value) } : { success: false, error: new Error("Invalid CRM settings") };
  },
  parse(value: unknown): CrmSettings {
    if (!isCrmSettings(value)) throw new Error("Invalid CRM settings");
    return structuredClone(value);
  },
};

export const defaultCrmSettings: CrmSettings = {
  company_name: "YKY Hair Care",
  timezone: "Asia/Shanghai",
  default_currency: "USD",
  follow_up_default_days: 3,
  email_signature: "Best regards,\nYKY Hair Care Sales Team",
  lead_scoring: { customer_reply: 10, product_interest: 10, estimated_quantity: 20, sample_requested: 20, quotation_requested: 20, whatsapp: 5, target_market: 5, inactive_7_days: -5, inactive_14_days: -10, inactive_30_days: -20 },
  prospect_scoring: { target_market: 10, industry_match: 15, website: 5, public_email: 10, whatsapp: 5, distributor: 20, brand_owner: 20, trade_record: 15, recent_trade: 10, product_match: 15, industry_mismatch: -30, b2c_individual: -40 },
  whatsapp_templates: whatsappTemplates.map(({ key, label, message }) => ({ key, label, message })),
  customer_sources: ["Google Ads", "Google SEO", "官网", "WhatsApp", "Email", "Facebook", "Instagram", "LinkedIn", "TikTok", "Alibaba", "Referral", "Manual", "Other"],
  product_interests: ["洗发水", "护发素", "发膜", "头皮精华", "护发精油", "造型产品", "染烫护理", "定制套装"],
};

const legacyTemplateKeys: Record<string, string> = { sample_follow_up: "sample_feedback" };
const requiredTemplateKeyByLabel = new Map(whatsappTemplates.flatMap((template) => [
  [template.label, template.key] as const,
  ...(template.key === "sample_feedback" ? [["样品跟进", template.key] as const] : []),
]));

export function normalizeWhatsAppTemplates(value: unknown): WhatsAppTemplateSetting[] {
  const source = Array.isArray(value) ? value : [];
  const normalized: WhatsAppTemplateSetting[] = [];
  const seen = new Set<string>();
  for (const item of source) {
    if (!item || typeof item !== "object") continue;
    const raw = item as Record<string, unknown>;
    const label = typeof raw.label === "string" ? raw.label.trim() : "";
    const message = typeof raw.message === "string" ? raw.message.trim() : "";
    const rawKey = typeof raw.key === "string" ? raw.key : "";
    const key = legacyTemplateKeys[rawKey] || requiredTemplateKeyByLabel.get(label) || rawKey;
    if (!key || seen.has(key) || !label || !message) continue;
    seen.add(key);
    normalized.push({ key, label, message });
  }
  for (const template of defaultCrmSettings.whatsapp_templates) {
    if (!seen.has(template.key)) normalized.push(structuredClone(template));
  }
  return normalized.slice(0, 50);
}

export function settingsFromPayload(payload: Record<string, unknown>): CrmSettings {
  const company = payload.company_profile as Record<string, unknown> | undefined;
  const followUp = payload.follow_up_defaults as Record<string, unknown> | undefined;
  const signature = payload.email_signature as Record<string, unknown> | undefined;
  const candidate = {
    company_name: company?.company_name ?? defaultCrmSettings.company_name,
    timezone: company?.timezone ?? defaultCrmSettings.timezone,
    default_currency: company?.default_currency ?? defaultCrmSettings.default_currency,
    follow_up_default_days: followUp?.cycle_days ?? defaultCrmSettings.follow_up_default_days,
    email_signature: signature?.signature ?? defaultCrmSettings.email_signature,
    lead_scoring: payload.lead_scoring ?? defaultCrmSettings.lead_scoring,
    prospect_scoring: payload.prospect_scoring ?? defaultCrmSettings.prospect_scoring,
    whatsapp_templates: normalizeWhatsAppTemplates(payload.whatsapp_templates),
    customer_sources: payload.customer_sources ?? defaultCrmSettings.customer_sources,
    product_interests: payload.product_interests ?? defaultCrmSettings.product_interests,
  };
  const result = crmSettingsSchema.safeParse(candidate);
  return result.success ? result.data : structuredClone(defaultCrmSettings);
}

export function settingsToPayload(settings: CrmSettings) {
  const value = crmSettingsSchema.parse({ ...settings, whatsapp_templates: normalizeWhatsAppTemplates(settings.whatsapp_templates) });
  return {
    company_profile: { company_name: value.company_name, timezone: value.timezone, default_currency: value.default_currency },
    follow_up_defaults: { cycle_days: value.follow_up_default_days },
    email_signature: { signature: value.email_signature },
    lead_scoring: value.lead_scoring,
    prospect_scoring: value.prospect_scoring,
    whatsapp_templates: value.whatsapp_templates,
    customer_sources: value.customer_sources,
    product_interests: value.product_interests,
  };
}

export function parseOptionLines(value: string) {
  return [...new Set(value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean))].slice(0, 100);
}

export function templatesToText(templates: WhatsAppTemplateSetting[]) {
  return templates.map((template) => `${template.label} | ${template.message.replace(/\s*\r?\n\s*/g, " ")}`).join("\n");
}

export function parseTemplateLines(value: string): WhatsAppTemplateSetting[] {
  return value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).slice(0, 50).map((line, index) => {
    const separator = line.indexOf("|");
    const label = (separator >= 0 ? line.slice(0, separator) : `模板 ${index + 1}`).trim();
    const message = (separator >= 0 ? line.slice(separator + 1) : line).trim();
    return { key: requiredTemplateKeyByLabel.get(label) || `template_${index + 1}`, label, message };
  });
}
